// TLZ-561. The wire contract between the widget and the agent. Mirrored in
// livekit-agent-worker/screenshare_protocol.py; both sides assert against
// fixtures/screenshare-protocol.fixture.json so a change on one side fails the other.
//
// Transport is LiveKit RPC plus participant attributes. Track publish and unpublish
// events remain the source of truth for media state; these messages carry intent,
// outcome and attribution, which the track events cannot express.
export const SCREENSHARE_PROTOCOL_VERSION = 1;
/** Agent to widget. */
export const RPC_REQUEST_CONSENT = 'screenshare.requestConsent';
export const RPC_STOP = 'screenshare.stop';
/** Widget to agent. */
export const RPC_NOTIFY = 'screenshare.notify';
/** Set by the widget once it knows whether this browser can capture a display. */
export const ATTR_CAPABLE = 'telzino.screenshare.capable';
/** Set by the agent from the session resolver. */
export const ATTR_ENABLED = 'telzino.screenshare.enabled';
/**
 * Set by the agent alongside `ATTR_ENABLED`: the surfaces org policy allows, comma-joined
 * in protocol order (`browser,window`). Empty when the agent retracts `enabled`. The widget
 * reads it with `parseAllowedSurfaces`, which falls back to every surface, so an agent that
 * predates this key still works.
 */
export const ATTR_ALLOWED_SURFACES = 'telzino.screenshare.allowed_surfaces';
/**
 * The canonical list, in preference order (least invasive first). The single definition
 * for this repo — it used to be declared three times (here implicitly via the type, plus
 * two runtime copies: `KNOWN_SURFACES` in embed-config-client.ts and `SURFACE_PREFERENCE`
 * in use-screenshare-session.ts), all required to agree by hand. Order matters here, not
 * just membership: `preferredSurface` walks this list to pick the least invasive surface
 * the caller's policy allows.
 *
 * Mirrored in fixtures/screenshare-protocol.fixture.json's enums.shareSurface (asserted in screenshare-protocol.test.ts) and in the worker's screenshare_protocol.py, checked for parity by the same fixture.
 */
export const SHARE_SURFACES = ['browser', 'window', 'monitor'];
/**
 * Parse the `ATTR_ALLOWED_SURFACES` value. Unknown entries are dropped, duplicates
 * collapse, and the result is in protocol order regardless of the wire order. Nothing
 * usable means the full list: this attribute is a hint to the picker, never enforcement.
 */
export function parseAllowedSurfaces(raw) {
    const wanted = new Set((raw !== null && raw !== void 0 ? raw : '')
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => SHARE_SURFACES.includes(entry)));
    const ordered = SHARE_SURFACES.filter((surface) => wanted.has(surface));
    return ordered.length ? ordered : [...SHARE_SURFACES];
}
/** Every message is version-stamped; a mismatched peer is ignored rather than guessed at. */
export function isCurrentVersion(payload) {
    return (payload === null || payload === void 0 ? void 0 : payload.v) === SCREENSHARE_PROTOCOL_VERSION;
}
